import { json } from '@codemirror/lang-json';
import { Decoration, EditorView, MatchDecorator, ViewPlugin, type DecorationSet, type ViewUpdate } from '@codemirror/view';
import CodeMirror from '@uiw/react-codemirror';
import { useMemo } from 'react';

// Highlight {{var}} and {{#if}}/{{/if}}/{{> snippet}} so prompt authors see the moving parts.
const varDeco = new MatchDecorator({
  regexp: /\{\{\s*[a-zA-Z_][\w.]*\s*\}\}|\{\{[#/>][^}]*\}\}/g,
  decoration: (m) => Decoration.mark({ class: /\{\{\s*[#/>]/.test(m[0]) ? 'cm-block' : 'cm-var' }),
});

const templateHighlight = ViewPlugin.fromClass(
  class {
    decorations: DecorationSet;
    constructor(view: EditorView) {
      this.decorations = varDeco.createDeco(view);
    }
    update(u: ViewUpdate) {
      this.decorations = varDeco.updateDeco(u, this.decorations);
    }
  },
  { decorations: (v) => v.decorations },
);

const base = EditorView.theme({ '&': { fontSize: '13px' }, '.cm-content': { fontFamily: 'var(--font-mono)', padding: '8px 0' }, '.cm-gutters': { display: 'none' } });

export const PromptEditor = ({ value, onChange, minHeight = '220px', readOnly, placeholder }: { value: string; onChange?: (v: string) => void; minHeight?: string; readOnly?: boolean; placeholder?: string }) => {
  const extensions = useMemo(() => [templateHighlight, base, EditorView.lineWrapping], []);
  return (
    <CodeMirror
      value={value}
      onChange={onChange}
      readOnly={readOnly}
      editable={!readOnly}
      minHeight={minHeight}
      maxHeight="70vh"
      placeholder={placeholder}
      basicSetup={{ lineNumbers: false, foldGutter: false, highlightActiveLine: false, autocompletion: false }}
      extensions={extensions}
    />
  );
};

export const JsonEditor = ({ value, onChange, minHeight = '160px', readOnly }: { value: string; onChange?: (v: string) => void; minHeight?: string; readOnly?: boolean }) => {
  const extensions = useMemo(() => [json(), base, EditorView.lineWrapping], []);
  return (
    <CodeMirror
      value={value}
      onChange={onChange}
      readOnly={readOnly}
      editable={!readOnly}
      minHeight={minHeight}
      maxHeight="60vh"
      basicSetup={{ lineNumbers: false, foldGutter: true, highlightActiveLine: false, autocompletion: false }}
      extensions={extensions}
    />
  );
};
